import { Component, OnInit } from '@angular/core';
import { RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { LoaderService } from './services/loader.service';
import { environment } from '../environments/environments';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AppLanguageService, TranslationService } from '@ace/ace';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  menu;
  title = 'ABG';
  private languageRequest = 0;
  constructor(
    private router: Router,
    private loader: LoaderService,
    private translate: TranslateService,
    private appLanguage: AppLanguageService,
    private aceTranslation: TranslationService
  ) {
    loader.hideLoader();
    router.events.subscribe((event: any) => {
      if (event instanceof RouteConfigLoadStart) {
        loader.showLoader();
      } else if (event instanceof RouteConfigLoadEnd) {
        loader.hideLoader();
      }
    })
  }

  ngOnInit(): void {
    this.loadTheme();
    this.syncTranslateLanguage();
  }

  /**
   * Keeps ngx-translate in step with the language picked through the ACE header.
   * AppLanguageService emits before its own dictionary finishes loading, so the ACE
   * dictionary is awaited first: components that only re-render on ngx-translate's
   * onLangChange (OnPush pages, ACE table headers) would otherwise redraw while the
   * ACE dictionary is still empty and stay in English.
   */
  private syncTranslateLanguage() {
    this.appLanguage.selectedLanguage$.subscribe(language => {
      const isoCode = (language?.isoCode || environment.constants.defaultLanguage).toLowerCase();
      void this.applyLanguage(isoCode);
    });
  }

  private async applyLanguage(isoCode: string): Promise<void> {
    // Switching again while a dictionary is still loading makes the older run finish last,
    // which is what left labels in two languages at once. Later runs win.
    const request = ++this.languageRequest;
    await this.aceTranslation.init(isoCode);
    if (request !== this.languageRequest) {
      return;
    }
    await firstValueFrom(this.translate.use(isoCode));
    if (request !== this.languageRequest) {
      return;
    }
    // Re-assert once the switch has settled: menus and ACE-rendered labels are resolved
    // when their own requests land, which can be after this point.
    this.translate.setTranslation(isoCode, this.translate.translations[isoCode] ?? {}, true);
  }

  loadTheme() {
    let theme = localStorage.getItem("selectedTheme")
    if (!theme) {
      theme = environment.constants.defaultTheme;
      localStorage.setItem("selectedTheme", theme);
    }
    if (theme == "theme1") {
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-2');
      const shadowColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-shadow-color');
      const chartLabelColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-chart-label-color');
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      document.documentElement.style.setProperty('--default-shadow-color', shadowColor);
      document.documentElement.style.setProperty('--default-chart-label-color', chartLabelColor);

      const backgroundColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-bg-color');
      document.documentElement.style.setProperty('--default-bg-color', backgroundColor);

      document.documentElement.style.setProperty('--white-color', '#fff');

    } else if (theme == "theme2") {
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-2');
      const shadowColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-shadow-color');
      const chartLabelColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-chart-label-color');
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      document.documentElement.style.setProperty('--default-shadow-color', shadowColor);
      document.documentElement.style.setProperty('--default-chart-label-color', chartLabelColor);

      const backgroundColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-bg-color');
      document.documentElement.style.setProperty('--default-bg-color', backgroundColor);


      const defaultColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--default-color-2');
      document.documentElement.style.setProperty('--option-default-2', defaultColor_2);

      document.documentElement.style.setProperty('--menu-bg-color', sourceColor);
      document.documentElement.style.setProperty('--Text-color', '#fff');
      document.documentElement.style.setProperty('--white-color', sourceColor);
      document.documentElement.style.setProperty('--grid-data-color', '#fff');
      // Make form labels and titles white in dark mode
      document.documentElement.style.setProperty('--label-color', '#fff');

    }
  }
}
